import { deleteApp, initializeApp } from "firebase/app";
import { createUserWithEmailAndPassword, getAuth, signOut } from "firebase/auth";

import firebaseApp from "@/apis/config/firebase";

/**
 * Crea la cuenta de Firebase Auth de un miembro del equipo SIN tocar la sesión
 * de quien la está creando.
 *
 * ### Por qué hace falta esta gimnasia
 *
 * `createUserWithEmailAndPassword` deja la sesión activa en la cuenta recién
 * creada. Sobre la instancia primaria de Auth eso significa que el
 * administrador que da de alta a alguien **queda logueado como esa persona** y
 * pierde su propia sesión. (Es lo que le pasa hoy al alta de usuarios normales,
 * ver `src/apis/auth/auth.ts:signUp`.)
 *
 * La solución estándar en cliente es una app de Firebase secundaria: se crea la
 * cuenta en su propio contexto de Auth, se cierra esa sesión y se destruye la
 * app. La sesión primaria no se entera de nada.
 *
 * La alternativa correcta a medio plazo es una Cloud Function con el Admin SDK,
 * que además permitiría crear la cuenta sin conocer nunca la contraseña. Cuando
 * exista, este archivo se sustituye por una llamada a ese endpoint y nada más
 * del flujo cambia.
 */

/** Nombre de la app secundaria. Se crea y se destruye en cada alta. */
const SECONDARY_APP_NAME = "staff-provisioning";

export interface CreateAuthAccountResult {
  uid: string;
}

function friendlyAuthError(error: unknown): string {
  const code =
    typeof error === "object" && error !== null && "code" in error
      ? String((error as { code: unknown }).code)
      : "";

  switch (code) {
    case "auth/email-already-in-use":
      return "Ya existe una cuenta con ese correo. Si es de esta persona, búscala en el listado en vez de crearla.";
    case "auth/invalid-email":
      return "El correo no tiene un formato válido.";
    case "auth/weak-password":
      return "La contraseña es demasiado débil: usa al menos 6 caracteres.";
    case "auth/operation-not-allowed":
      return "El acceso con correo y contraseña no está habilitado en Firebase.";
    default:
      return error instanceof Error
        ? error.message
        : "No se pudo crear la cuenta de acceso.";
  }
}

/**
 * Crea la cuenta de acceso y devuelve su uid.
 *
 * No escribe nada en Firestore: el perfil de equipo lo crea quien llama, con
 * este uid.
 */
export async function createAuthAccount(
  email: string,
  password: string
): Promise<CreateAuthAccountResult> {
  const secondaryApp = initializeApp(firebaseApp.options, SECONDARY_APP_NAME);
  const secondaryAuth = getAuth(secondaryApp);

  try {
    const credential = await createUserWithEmailAndPassword(
      secondaryAuth,
      email,
      password
    );

    return { uid: credential.user.uid };
  } catch (error) {
    throw new Error(friendlyAuthError(error));
  } finally {
    // Cerrar la sesión secundaria y destruir la app pase lo que pase: si
    // quedara viva, el siguiente alta fallaría por nombre de app duplicado.
    try {
      await signOut(secondaryAuth);
    } catch {
      // Si no había sesión que cerrar, no hay nada que hacer.
    }
    await deleteApp(secondaryApp);
  }
}
